/**
 * ChatThreadV2 — the V2 thread view (bead terra-design-v2-chat-yub.5, spec
 * p1.s2): asymmetric bubbles, stable ids, anchored autoscroll.
 *
 * Mounted by `FlowChatPanelV2`'s `ChatSessionViewV2` INSIDE the same
 * `DurableStreamRuntimeProvider externalStream={…}` V1 mounts — this
 * component takes no props and reads everything from that provider's
 * assistant-ui runtime context (`ThreadPrimitive`/`MessagePrimitive`) plus
 * `useKeystoneChatStream()`. Never mount this without a `DurableStreamRuntimeProvider`
 * ancestor.
 *
 * Q3 (render path) — DECIDED, Option (A): built from `@assistant-ui/react`
 * primitives (`ThreadPrimitive.Viewport autoScroll`, `ThreadPrimitive.Messages`,
 * `MessagePrimitive.Parts`), never a second raw-`StreamMessage[]` rendering
 * path. See this bead's own description ("Resolved — Phase 1 escalation
 * answers") and coding-standards.md's layer-boundary rule.
 *
 * Turn-placeholder conflict (architect pass, 2026-09-30) — DECIDED IN
 * PRINCIPLE, Option (b), NOT IMPLEMENTED IN THIS BEAD: if a placeholder row
 * is added, it must live OUTSIDE `ThreadPrimitive.Messages` (a footer
 * element, hand-off at the first delta, no claimed DOM identity across that
 * specific instant) — never a DSRP-side turnId-keyed row (option a), which
 * would require `useConversationStream.ts` (read-only to this epic per
 * coding-standards.md) to emit a stable pre-delta row keyed by `turnId`, a
 * row no frame in the real trace carries in advance of the first delta.
 * This bead ships NO placeholder at all, for a second, independent reason
 * found while writing this bead's own guardrail test against the real
 * trace: the natural running-condition signal, `useKeystoneChatStream().isRunning`,
 * is WRONG for exactly this shape of trace. `useConversationStream`'s
 * `replay-done` handler infers `isRunning` from whether the LAST message
 * observed during replay has `type === 'user'`; in the real trace, replay
 * ends on a `status` frame (`"Resolving account..."`) that arrived after
 * the user's question but before `replay-done` — so `isRunning` is set
 * `false` right at replay-done and stays `false` through the entire visible
 * streaming turn (the delta-accumulation path never sets `isRunning` true).
 * A placeholder gated on that flag would never appear for this realistic
 * "stream reconnects/opens mid-turn" shape. A message-derived alternative
 * (walk `s.thread.messages` backward from the end for the nearest non-
 * `status` row) is buildable without that dependency, but needs an
 * additional "is this session actually frozen/stopped" signal to avoid
 * showing "Thinking…" forever on a truly-stopped session with a dangling
 * unanswered question — `ChatThreadV2` takes no props today and has no
 * such signal. Recorded here rather than shipped half-correct; bead .9
 * ("Render the live step list...") owns the live-steps header this
 * eventually feeds and should resolve both open points before building it.
 * DOM identity across the delta→final transition of the REAL assistant row
 * (not a placeholder) IS verified and true, since `useConversationStream`
 * keeps the same `messageId` (`parentMessageId`) for both — see that
 * hook's own comments.
 *
 * `status` frames: `convertMessage` (DurableStreamRuntimeProvider.tsx)
 * currently maps every `status` `StreamMessage` to a `role: 'assistant'`,
 * `status: { type: 'running' }` row — indistinguishable at the
 * `ThreadMessageLike` level from the real synthetic streaming-assistant row
 * convertMessage also produces. `convertMessage`'s `status` case now tags
 * `metadata.custom.streamType: 'status'` (additive-only — V1's `Thread.tsx`
 * never reads `metadata.custom`) so `AssistantBubble` below can skip
 * rendering it as a message bubble ("in V2 show the latest status text in
 * the live-steps header, not as a message row, so the thread does not
 * flicker" — this bead's own "What to build" section; the live-steps
 * header widget itself is bead .9's scope, not this file's).
 *
 * Turn-placeholder — IMPLEMENTED (bead terra-design-v2-chat-yub.9, resolving
 * the "Turn-placeholder conflict" note above). `ChatRunStepsFooter` below is
 * the Option (b) footer element bead .5 deferred: it lives OUTSIDE
 * `ThreadPrimitive.Messages` as a sibling after it, renders the
 * `useOpenTransparencyTurn()` record (the turn still awaiting its own
 * turnId-driven assistant association) while `deriveTransparencyTurns.ts`
 * has not yet attributed a synthetic streaming message to it, and hands off
 * to `AssistantBubble`'s own `ChatRunSteps` the instant that attribution
 * lands (`provisionalAssistantMessageId`, bead .9's additive field) — no
 * claimed DOM identity across that handoff, exactly as the deferred note
 * specified.
 *
 * The second deferred point — the thread-level `isRunning` signal being
 * wrong for this trace shape — is NOT fully resolved, and `resolveOutcome`
 * below says so rather than silently relying on a signal already proven
 * wrong. `useKeystoneChatStream().isRunning` is reliable for a fresh ask in
 * the SAME mount (`useConversationStream.ts`'s `send` sets it synchronously),
 * but bead .5's own docstring above traced the exact case where it's wrong:
 * a stream that reconnects/opens mid-turn latches `isRunning` from whichever
 * message `replay-done` landed on, and never revisits it until the next
 * `user`/`assistant`/`error` frame — which for THIS bead's own real-trace
 * fixture is `false` for the entire visible run (confirmed by rendering it:
 * the footer read "Stopped · N steps" throughout a run that was very much
 * alive and growing). Since `useConversationStream.ts` is read-only to this
 * epic and there is no OTHER stopped-vs-running signal available here (the
 * real "frozen on stop" signal lives in `FlowChatSessionsProvider.tsx`,
 * itself read-only to this epic per coding-standards.md), `resolveOutcome`
 * treats any open, non-errored turn as `'running'` — correct for both the
 * normal live-ask case and this trace's reconnect-mid-turn case, at the
 * known cost of not surfacing `'stopped'` for a turn that goes genuinely
 * silent with no further signal. `'stopped'` stays a real, independently
 * tested `ChatRunSteps` capability (unit-tested directly) for whenever a
 * reliable signal exists; this wiring just doesn't fabricate one from a
 * flag already shown to lie. Flagged in this bead's Implementation Notes.
 */
import { createContext, useContext, useMemo } from 'react';
import {
  MessagePrimitive,
  ThreadPrimitive,
  useAssistantState,
  useMessage,
} from '@assistant-ui/react';
import { Text } from '@clay-run/glaze/text';
import { useKeystoneChatStream } from '../../../account-view/keystone/DurableStreamRuntimeProvider';
import {
  useOpenTransparencyTurn,
  useTransparencyTurn,
  useTransparencyTurnIdForMessage,
} from '../../../shared/TransparencyStepsContext';
import type { TransparencyTurn } from '../../../shared/TransparencyStepsContext';
import { ChatMarkdown } from './ChatMarkdown';
import { ChatRunSteps, type ChatRunStepsOutcome } from './ChatRunSteps';
import { SaveAsNoteButton } from '../notes/SaveAsNoteButton';

/**
 * The account this thread is about, for "Save as note". Read by `AssistantBubble`
 * (rendered through assistant-ui's component slots, so a prop cannot reach it);
 * `null` means no record context, so no button.
 */
const ChatNoteAccountContext = createContext<string | null>(null);

/**
 * Shared by the footer placeholder and `AssistantBubble`'s still-open case.
 * See this file's header comment ("The second deferred point...") for why
 * this does not read `useKeystoneChatStream().isRunning`.
 */
function resolveOutcome(turn: TransparencyTurn | null): ChatRunStepsOutcome {
  return turn?.errored ? 'error' : 'running';
}

// UserBubble — right-aligned, bubbled (bead's prototype citation: `ml-8
// self-end rounded-2xl rounded-br-md bg-bg-secondary px-3 py-2` + `Text
// size="sm"`).

function UserBubble() {
  const messageId = useMessage((m) => m.id);
  return (
    <div
      key={messageId}
      data-message-id={messageId}
      data-role="user"
      className="ml-8 self-end rounded-2xl rounded-br-md bg-bg-secondary px-3 py-2"
    >
      <MessagePrimitive.Parts components={{ Text: UserBubbleText }} />
    </div>
  );
}

function UserBubbleText() {
  const text = useMessagePartTextContent();
  return <Text size="sm">{text}</Text>;
}

// AssistantBubble — unbubbled, left-aligned (bead's prototype citation:
// `flex flex-col gap-2 pr-6`), live step block ABOVE the markdown body
// (bead .9: resolves this message's `turnId` and renders `ChatRunSteps`
// with it, once the turn-placeholder footer below has handed off).

function AssistantBubble() {
  const messageId = useMessage((m) => m.id);
  const streamType = useMessage((m) => (m.metadata.custom as { streamType?: string }).streamType);
  // Not yet finalized (no `stats` yet) — see `AssistantBubbleText`'s own
  // doc comment for the full three-completion-transition rationale this
  // mirrors. Hooks stay unconditional above the `streamType` early return
  // below (Rules of Hooks).
  const messageIsRunning = useMessage((m) => m.status?.type === 'running');
  const turnId = useTransparencyTurnIdForMessage(messageId, messageIsRunning);
  const turn = useTransparencyTurn(turnId);
  const noteAccountId = useContext(ChatNoteAccountContext);
  const answerText = useMessage((m) =>
    m.content
      .map((part) => (part.type === 'text' ? part.text : ''))
      .join('')
      .trim(),
  );

  // Skip the "status ticker" row entirely — see this file's header comment.
  // `metadata.custom.streamType` is set only on `status`-sourced rows.
  if (streamType === 'status') return null;

  const outcome: ChatRunStepsOutcome = !messageIsRunning ? 'done' : resolveOutcome(turn);

  return (
    <div key={messageId} data-message-id={messageId} data-role="assistant" className="group/answer flex flex-col gap-2 pr-6">
      {turnId && <ChatRunSteps turnId={turnId} outcome={outcome} />}
      <MessagePrimitive.Parts components={{ Text: AssistantBubbleText }} />
      {!messageIsRunning && noteAccountId && answerText && (
        <div className="opacity-0 transition-opacity focus-within:opacity-100 group-hover/answer:opacity-100">
          <SaveAsNoteButton
            text={answerText}
            defaultTarget={{ kind: 'account', id: noteAccountId, accountId: noteAccountId }}
          />
        </div>
      )}
    </div>
  );
}

function AssistantBubbleText() {
  const { isRunning: threadIsRunning } = useKeystoneChatStream();
  // Streaming iff this message hasn't been finalized (no `stats` yet —
  // surfaced here as assistant-ui's `status.type === 'running'`, set by
  // `convertMessage` for exactly the no-`stats` case) AND the stream as a
  // whole is still running — the second half of this guard is what turns
  // a frozen/stopped mid-run message's cursor off instead of leaving it
  // streaming forever (see `ChatMarkdown`'s own doc comment for the three
  // completion transitions this covers).
  const messageIsRunning = useMessage((m) => m.status?.type === 'running');
  const text = useMessagePartTextContent();
  return <ChatMarkdown streaming={messageIsRunning && threadIsRunning}>{text}</ChatMarkdown>;
}

// useMessagePartTextContent — reads the current text part's live text
// (smooth streaming included) without assuming a `text` prop is passed in,
// since `MessagePrimitive.Parts`'s `Text` component slot receives no props
// (assistant-ui reads the current part from context internally — see
// `node_modules/@assistant-ui/react/.../MessagePartText.js`, the primitive
// this text slot is documented to use).
function useMessagePartTextContent(): string {
  return useAssistantState((s) => {
    const part = s.part;
    if (part.type !== 'text' && part.type !== 'reasoning') return '';
    return part.text;
  });
}

// ChatRunStepsFooter — the turn-placeholder row (bead .9, see this file's
// header comment). Lives OUTSIDE `ThreadPrimitive.Messages`, renders only
// while a turn is open (no assistantMessageId) AND no synthetic streaming
// message has been attributed to it yet (`provisionalAssistantMessageId`
// unset) — the instant that attribution lands, `AssistantBubble` picks the
// same turnId up on its own real row, and this footer naturally stops
// matching (its `useOpenTransparencyTurn` lookup excludes any turn with an
// `assistantMessageId`, and this still-open turn's `provisionalAssistantMessageId`
// gate takes over before that).

function ChatRunStepsFooter() {
  const openTurn = useOpenTransparencyTurn();

  if (!openTurn || openTurn.provisionalAssistantMessageId) return null;

  return <ChatRunSteps turnId={openTurn.turnId} outcome={resolveOutcome(openTurn)} />;
}

// ChatThreadV2 (root)

export function ChatThreadV2({ accountId = null }: { accountId?: string | null } = {}) {
  const components = useMemo(
    () => ({ UserMessage: UserBubble, AssistantMessage: AssistantBubble }),
    [],
  );

  return (
    <ChatNoteAccountContext.Provider value={accountId}>
      <ThreadPrimitive.Viewport
        autoScroll
        data-testid="chat-thread-v2"
        className="flex-1 min-h-0 overflow-y-auto px-4 py-3 flex flex-col gap-3"
      >
        <ThreadPrimitive.Messages components={components} />
        <ChatRunStepsFooter />
      </ThreadPrimitive.Viewport>
    </ChatNoteAccountContext.Provider>
  );
}
